// src/utils/helpers.js

/**
 * Formater un montant en FCFA
 */
export const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 FCFA';
    return `${parseInt(amount).toLocaleString('fr-FR')} FCFA`;
  };
  
  /**
   * Formater une date
   */
  export const formatDate = (date, options = {}) => {
    if (!date) return '';
    
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options
    };
    
    return new Date(date).toLocaleDateString('fr-FR', defaultOptions);
  };
  
  /**
   * Formater une date et heure
   */
  export const formatDateTime = (date) => {
    if (!date) return '';
    
    return new Date(date).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  /**
   * Formater une heure
   */
  export const formatTime = (date) => {
    if (!date) return '';
    
    return new Date(date).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  /**
   * Calculer le temps écoulé depuis une date
   */
  export const timeAgo = (date) => {
    if (!date) return '';
    
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' an(s)';
    
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' mois';
    
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' jour(s)';
    
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' heure(s)';
    
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' minute(s)';
    
    return Math.floor(seconds) + ' seconde(s)';
  };
  
  /**
   * Tronquer un texte
   */
  export const truncate = (text, length = 50) => {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  };
  
  /**
   * Capitaliser la première lettre
   */
  export const capitalize = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };
  
  /**
   * Obtenir les initiales d'un nom
   */
  export const getInitials = (name) => {
    if (!name) return '';
    
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  /**
   * Calculer un pourcentage
   */
  export const calculatePercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return Math.round((value / total) * 100);
  };
  
  /**
   * Générer une couleur aléatoire
   */
  export const randomColor = () => {
    const colors = [
      '#3b82f6', '#ef4444', '#10b981', '#f59e0b', 
      '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };
  
  /**
   * Valider un email
   */
  export const isValidEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };
  
  /**
   * Valider un numéro de téléphone
   */
  export const isValidPhone = (phone) => {
    const regex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    return regex.test(phone);
  };
  
  /**
   * Calculer la distance entre deux points GPS (en km)
   */
  export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };
  
  /**
   * Formater une distance
   */
  export const formatDistance = (distance) => {
    if (!distance) return '0 km';
    if (distance < 1) return `${Math.round(distance * 1000)} m`;
    return `${distance.toFixed(1)} km`;
  };
  
  /**
   * Obtenir la classe CSS pour un statut
   */
  export const getStatusClass = (status) => {
    const statusClasses = {
      pending: 'bg-gray-100 text-gray-800',
      assigned: 'bg-blue-100 text-blue-800',
      accepted: 'bg-cyan-100 text-cyan-800',
      picked_up: 'bg-purple-100 text-purple-800',
      in_transit: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-red-100 text-red-800',
      returned: 'bg-yellow-100 text-yellow-800'
    };
    
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };
  
  /**
   * Débounce une fonction
   */
  export const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };
  
  /**
   * Copier dans le presse-papier
   */
  export const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.error('Erreur copie:', err);
      return false;
    }
  };
  
  /**
   * Télécharger un fichier
   */
  export const downloadFile = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  /**
   * Compresser une image
   */
  export const compressImage = (file, maxWidth = 1024, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              }));
            },
            'image/jpeg',
            quality
          );
        };
        
        img.onerror = reject;
      };
      
      reader.onerror = reject;
    });
  };
  
  /**
   * Vérifier si l'utilisateur est en ligne
   */
  export const isOnline = () => {
    return navigator.onLine;
  };
  
  /**
   * Obtenir le type d'appareil
   */
  export const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return "tablet";
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return "mobile";
    }
    return "desktop";
  };
  
  /**
   * Formater un numéro de suivi
   */
  export const formatTrackingNumber = (trackingNumber) => {
    if (!trackingNumber) return '';
    // Format: XXXX-XXXX-XXXX
    return trackingNumber.match(/.{1,4}/g)?.join('-') || trackingNumber;
  };
  
  /**
   * Générer un ID unique
   */
  export const generateUniqueId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };
  
  /**
   * Nettoyer les paramètres d'URL
   */
  export const cleanUrlParams = (params) => {
    const cleaned = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        cleaned[key] = params[key];
      }
    });
    return cleaned;
  };
  
  export default {
    formatCurrency,
    formatDate,
    formatDateTime,
    formatTime,
    timeAgo,
    truncate,
    capitalize,
    getInitials,
    calculatePercentage,
    randomColor,
    isValidEmail,
    isValidPhone,
    calculateDistance,
    formatDistance,
    getStatusClass,
    debounce,
    copyToClipboard,
    downloadFile,
    compressImage,
    isOnline,
    getDeviceType,
    formatTrackingNumber,
    generateUniqueId,
    cleanUrlParams
  };